import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, AudioLines, Navigation } from "lucide-react";

const shots = [
  {
    img: "/products/ctx-guide.jpg",
    kicker: "Never lost",
    title: "Guided safely home.",
    watch: { avatar: "/products/parent-car.jpg", name: "Mom", note: "On a break at work", status: "Ethan · en route home" },
    speech: "Keep walking straight, then turn right at the next street.",
  },
  {
    img: "/products/mom-work.jpg",
    kicker: "Peace of mind at work",
    title: "Know the moment they move.",
    notify: { name: "Emily", place: "Publix grocery store" },
  },
  { img: "/products/ctx-child.jpg", kicker: "For the little explorers", title: "Waiting for the bus. Never alone." },
  { img: "/products/ctx-teen.jpg", kicker: "For the free spirits", title: "Freedom to roam." },
  { img: "/products/ctx-adults.jpg", kicker: "For the ones who raised us", title: "Independence, protected." },
];

export const ReserveBand = () => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % shots.length), []);
  useEffect(() => {
    const t = setInterval(next, 3200);
    return () => clearInterval(t);
  }, [next]);

  return (
    <section data-testid="reserve-band" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="rounded-[2rem] bg-[#F5F5F7] p-5 sm:p-8 md:p-10 flex flex-col md:flex-row items-center gap-8 md:gap-12">
          {/* Square slideshow */}
          <div className="w-full md:w-[340px] lg:w-[400px] shrink-0">
            <div className="relative w-full aspect-square rounded-3xl overflow-hidden bg-zinc-200 shadow-[0_20px_50px_rgb(0,0,0,0.08)]">
              {shots.map((s, i) => (
                <img
                  key={s.img}
                  src={s.img}
                  alt={s.title}
                  aria-hidden={i !== index}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1100ms] ease-out"
                  style={{ opacity: i === index ? 1 : 0 }}
                />
              ))}

              {/* Scrim for caption legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />

              {/* Live-watching notification card */}
              {shots[index].watch && (
                <div key={`w-${index}`} className="hero-reveal absolute z-20 top-3 right-3 w-[52%] max-w-[190px] rounded-xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-xl p-2">
                  <div className="flex items-center gap-2">
                    <img src={shots[index].watch.avatar} alt={shots[index].watch.name} className="w-7 h-7 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500/50" />
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                        </span>
                        <span className="text-[10px] font-semibold text-zinc-900 truncate">{shots[index].watch.name} · Watching live</span>
                      </div>
                      <div className="text-[9px] text-zinc-500 truncate">{shots[index].watch.note}</div>
                    </div>
                  </div>
                  <div className="mt-1.5 rounded-md overflow-hidden relative">
                    <img src="/products/app-map.jpg" alt="Live location" className="w-full h-9 object-cover" />
                    <span className="absolute bottom-0.5 left-1 text-[8px] font-medium text-zinc-800 bg-white/80 px-1 py-px rounded">{shots[index].watch.status}</span>
                  </div>
                </div>
              )}

              {/* Voice guidance speech bubble */}
              {shots[index].speech && (
                <div key={`s-${index}`} className="hero-reveal absolute z-20 top-[38%] left-3 w-[58%] max-w-[210px]">
                  <div className="rounded-xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-xl px-2.5 py-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="w-4 h-4 rounded bg-zinc-900 text-white flex items-center justify-center">
                        <AudioLines className="w-2.5 h-2.5" />
                      </span>
                      <span className="text-[8px] font-semibold uppercase tracking-[0.12em] text-zinc-500">Vsense · Voice guidance</span>
                      <span className="ml-auto flex items-end gap-[2px] h-2.5">
                        {[0, 0.15, 0.3, 0.2].map((d, i) => (
                          <span key={i} className="eq-bar bg-zinc-900" style={{ animationDelay: `${d}s` }} />
                        ))}
                      </span>
                    </div>
                    <p className="text-[11px] font-medium text-zinc-900 leading-snug">
                      &ldquo;{shots[index].speech}&rdquo;
                    </p>
                  </div>
                </div>
              )}

              {/* Push notification card */}
              {shots[index].notify && (
                <div key={`n-${index}`} className="hero-reveal absolute z-20 top-3 right-3 w-[58%] max-w-[210px]">
                  <div className="rounded-xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-xl p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="w-4 h-4 rounded bg-zinc-900 text-white flex items-center justify-center">
                        <Navigation className="w-2.5 h-2.5" />
                      </span>
                      <span className="text-[8px] font-semibold text-zinc-500">VSENSE</span>
                      <span className="ml-auto text-[8px] text-zinc-400">now</span>
                    </div>
                    <p className="text-[11px] font-semibold text-zinc-900 leading-snug">{shots[index].notify.name} started a navigation</p>
                    <p className="text-[10px] text-zinc-600 leading-snug">Destination: {shots[index].notify.place}</p>
                    <div className="mt-1 flex items-center gap-1 text-[9px] font-medium text-emerald-600">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500/50" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                      </span>
                      Live · following along
                    </div>
                  </div>
                </div>
              )}

              {/* Caption box (per slide, with reveal effect) */}
              <div key={`cap-${index}`} className="hero-reveal absolute left-4 right-4 bottom-10 md:bottom-11 z-10">
                <span className="inline-block text-[10px] uppercase tracking-[0.25em] font-medium px-2.5 py-1 rounded-full bg-white/90 text-zinc-900">
                  {shots[index].kicker}
                </span>
                <h3 className="mt-2.5 font-display text-xl md:text-2xl font-extrabold tracking-tight text-white leading-tight drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)]">
                  {shots[index].title}
                </h3>
              </div>

              {/* dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                {shots.map((s, i) => (
                  <button
                    key={s.img}
                    data-testid={`reserve-band-dot-${i}`}
                    onClick={() => setIndex(i)}
                    aria-label={`Show image ${i + 1}`}
                    className="h-1.5 rounded-full transition-all duration-300"
                    style={{
                      width: i === index ? 22 : 6,
                      backgroundColor: i === index ? "#ffffff" : "rgba(255,255,255,0.6)",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Text + CTA */}
          <div className="flex-1 text-center md:text-left">
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">
              <span>Limited launch offer</span>
              <span className="text-emerald-400">·</span>
              <span>No credit card required</span>
            </span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-900 leading-[1.05]">
              Free VIP Access. Save 40%.
            </h2>
            <p className="mt-4 text-base md:text-lg text-zinc-500 leading-relaxed max-w-xl md:mx-0 mx-auto">
              Claim your Vsense glasses today — completely free, no card required. Lock in an exclusive
              <span className="font-semibold text-zinc-900"> 40% launch discount</span> that's yours to keep the moment preorders open.
            </p>
            <div className="mt-8 flex items-center justify-center md:justify-start">
              <button
                data-testid="reserve-band-cta"
                onClick={() => navigate("/shop")}
                className="group inline-flex items-center gap-2 bg-zinc-900 text-white text-sm md:text-base font-semibold px-8 py-3.5 rounded-full hover:bg-zinc-800 active:scale-[0.98] transition-all duration-200"
              >
                Join VIP — Free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
